﻿type SpinnerConfig = {
  min: number;
  max: number;
  step: number;
  autoPost: {
    enabled: boolean;
    delay: number;
  };
};

/**
 * Manages numeric input spinners with increment/decrement buttons
 */
export class SpinnerController {
  private spinners: NodeListOf<HTMLElement>;
  private activeTimeouts: Map<HTMLInputElement, number> = new Map();

  constructor(private spinnerSelector: string = '[data-spinner]') {
    this.spinners = document.querySelectorAll<HTMLElement>(spinnerSelector);
    this.initialize();
  }

  private initialize(): void {
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      this.initSpinners();
    } else {
      document.addEventListener('DOMContentLoaded', () => this.initSpinners());
    }
  }

  /**
   * Initialize all spinner components
   */
  public initSpinners(): void {
    this.spinners = document.querySelectorAll<HTMLElement>(this.spinnerSelector);
    this.spinners.forEach(spinner => this.setupSpinner(spinner));
  }

  /**
   * Set up an individual spinner component
   */
  private setupSpinner(spinner: HTMLElement): void {
    const downBtn = spinner.querySelector<HTMLButtonElement>('[data-spin="down"]');
    const upBtn = spinner.querySelector<HTMLButtonElement>('[data-spin="up"]');
    const input = spinner.querySelector<HTMLInputElement>('input');

    if (!downBtn || !upBtn || !input) return;

    const config = this.getSpinnerConfig(input);

    downBtn.addEventListener('click', () => this.adjustValue(input, -config.step, config));
    upBtn.addEventListener('click', () => this.adjustValue(input, config.step, config));

    input.addEventListener('change', () => {
      this.clampValue(input, config);
      if (config.autoPost.enabled) {
        this.scheduleFormSubmit(input, config.autoPost.delay);
      }
    });

    // Initial clamp in case input has invalid initial value
    this.clampValue(input, config);
  }

  /**
   * Get configuration for a spinner from its input attributes
   */
  private getSpinnerConfig(input: HTMLInputElement): SpinnerConfig {
    return {
      min: Number(input.getAttribute('min')) || 0,
      max: Number(input.getAttribute('max')) || Infinity,
      step: Number(input.getAttribute('step')) || 1,
      autoPost: {
        enabled: input.hasAttribute('data-autopost'),
        delay: parseInt(input.getAttribute('data-autopost') || '1000')
      }
    };
  }

  /**
   * Adjust the input value by specified amount
   */
  private adjustValue(input: HTMLInputElement, delta: number, config: SpinnerConfig): void {
    const currentValue = Number(input.value) || config.min;
    const newValue = delta > 0
      ? Math.min(currentValue + delta, config.max)
      : Math.max(currentValue + delta, config.min);

    input.value = newValue.toString();
    input.dispatchEvent(new Event('change'));
  }

  /**
   * Ensure value stays within bounds and snaps to step increments
   */
  private clampValue(input: HTMLInputElement, config: SpinnerConfig): void {
    let value = Number(input.value);

    if (isNaN(value)) {
      value = config.min;
    }

    value = Math.min(Math.max(value, config.min), config.max);
    value = Math.floor(value / config.step) * config.step;

    if (Number(input.value) !== value) {
      input.value = value.toString();
    }
  }

  /**
   * Schedule form submission after delay
   */
  private scheduleFormSubmit(input: HTMLInputElement, delay: number): void {
    // Clear any pending timeout
    const existingTimeout = this.activeTimeouts.get(input);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new timeout
    const timeoutId = setTimeout(() => {
      const form = input.closest('form');
      form?.requestSubmit();
      this.activeTimeouts.delete(input);
    }, delay) as unknown as number;

    this.activeTimeouts.set(input, timeoutId);
  }

  /**
   * Static initialization helper
   */
  public static init(selector: string = '[data-spinner]'): SpinnerController {
    return new SpinnerController(selector);
  }
}

// Auto-initialize when imported
SpinnerController.init();
